import { isServer } from '@cointracker/ui';
import classNames from 'clsx';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { H3 } from '../../typography';
import {
  marqueGroup,
  marquee,
  marqueeContainer,
  partnerItem,
} from './ScrollingLogos.css';
import { ScrollingDirection } from './constants';

interface ScrollingLogosProps {
  direction?: ScrollingDirection;
  partners: PartnerItem[];
  autoplay?: boolean;
  slowdown?: boolean;
}

export const ScrollingLogos = ({
  direction,
  partners,
  slowdown = false,
  autoplay = true,
}: ScrollingLogosProps) => {
  const [multiplier, setMultiplier] = useState(1);
  const marqueeRef = useRef<HTMLDivElement>(null);
  const calculateWidth = useCallback(() => {
    if (marqueeRef.current) {
      const marqueeRect = marqueeRef.current.getBoundingClientRect();
      const containerWidth = document.body.clientWidth;
      const marqueeWidth = marqueeRect.width;

      setMultiplier(
        marqueeWidth < containerWidth
          ? Math.ceil(containerWidth / marqueeWidth)
          : 1,
      );
    }
  }, [marqueeRef]);

  // we want to duplicate the marquee so that there are no gaps between the logos. If you are wondering how it helps
  // set the multiplier to always 1 and check on a bigger screen.
  const multiplyChildren = useCallback(
    (multiplier: number) => {
      return [
        ...Array(
          Number.isFinite(multiplier) && multiplier >= 0 ? multiplier : 0,
        ),
      ].map((_, i) => (
        <div
          className={classNames(marqueGroup, {
            slowdown,
          })}
          data-direction={direction}
          data-autoplay={autoplay}
          key={i}
        >
          {partners.map((partner) => (
            <PartnerLogo
              key={partner.text}
              logoUrl={partner.logoUrl}
              text={partner.text}
            />
          ))}
        </div>
      ));
    },
    [partners, autoplay, direction, slowdown],
  );

  useEffect(() => {
    if (isServer()) {
      return;
    }
    calculateWidth();
  }, [calculateWidth, partners]);

  if (isEmpty(partners)) {
    return null;
  }

  return (
    <div className={marqueeContainer}>
      <div className={marquee}>
        <div
          className={classNames(marqueGroup, {
            slowdown,
          })}
          data-direction={direction}
          data-autoplay={autoplay}
          ref={marqueeRef}
        >
          {partners.map((partner) => (
            <PartnerLogo
              key={partner.text}
              logoUrl={partner.logoUrl}
              text={partner.text}
            />
          ))}
        </div>
        {multiplyChildren(multiplier)}
      </div>
    </div>
  );
};

export interface PartnerItem {
  logoUrl: string;
  text: string;
}
const PartnerLogo = ({ logoUrl, text }: PartnerItem) => {
  return (
    <div className={partnerItem}>
      <img src={logoUrl} alt={`${text} Logo`} width={50} height={50} />
      <H3>{text}</H3>
    </div>
  );
};
